'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Card, ContentCard } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { makeUserCacheKey, readCachedData, writeCachedData } from '@/lib/client-cache';
import { startVisibilityAwarePolling } from '@/lib/polling';

const categories = [
  { id: 'all', label: 'All Resources' },
  { id: 'college', label: 'College Prep' },
  { id: 'career', label: 'Career Planning' },
  { id: 'academic', label: 'Academic Success' },
  { id: 'wellness', label: 'Wellness' },
];

interface GuidanceResource {
  id: number;
  title: string;
  description: string;
  category: string;
  type: string;
  readTime: string;
  featured: boolean;
}

interface StudentGuidanceCachePayload {
  resources: GuidanceResource[];
}

const STUDENT_GUIDANCE_CACHE_TTL_MS = 3 * 60 * 1000;

const categoryStyleMap: Record<
  string,
  {
    label: string;
    textColor: string;
    accent: string;
    iconBox: string;
    iconColor: string;
  }
> = {
  college: {
    label: 'College Prep',
    textColor: 'text-primary',
    accent: 'bg-primary',
    iconBox: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  career: {
    label: 'Career Planning',
    textColor: 'text-secondary',
    accent: 'bg-secondary',
    iconBox: 'bg-secondary/10',
    iconColor: 'text-secondary',
  },
  academic: {
    label: 'Academic Success',
    textColor: 'text-accent',
    accent: 'bg-accent',
    iconBox: 'bg-accent/10',
    iconColor: 'text-accent',
  },
  wellness: {
    label: 'Wellness',
    textColor: 'text-success',
    accent: 'bg-success',
    iconBox: 'bg-success/10',
    iconColor: 'text-success',
  },
};

function estimateReadTime(text: string) {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 180));
  return `${minutes} min`;
}

function formatType(value: string) {
  if (!value) return 'Guide';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function StudentGuidancePage() {
  const { user } = useAuth();
  const [resources, setResources] = useState<GuidanceResource[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [loadResourcesError, setLoadResourcesError] = useState('');
  const [hasWarmCache, setHasWarmCache] = useState(false);
  const [isCacheHydrated, setIsCacheHydrated] = useState(false);
  const [hasLoadedFromServer, setHasLoadedFromServer] = useState(false);
  const loadRequestIdRef = useRef(0);
  const resourcesRef = useRef<GuidanceResource[]>([]);
  const hasWarmCacheRef = useRef(false);
  const emptyFetchStreakRef = useRef(0);
  const cacheKey = useMemo(
    () => (user?.id ? makeUserCacheKey('student-guidance', user.id, user.schoolId) : null),
    [user?.id, user?.schoolId]
  );

  useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  useEffect(() => {
    hasWarmCacheRef.current = hasWarmCache;
  }, [hasWarmCache]);

  useLayoutEffect(() => {
    setIsCacheHydrated(false);
    setHasLoadedFromServer(false);
    setLoadResourcesError('');

    if (!cacheKey) {
      setResources([]);
      setHasWarmCache(false);
      setIsCacheHydrated(true);
      return;
    }

    const cached = readCachedData<StudentGuidanceCachePayload>(
      cacheKey,
      STUDENT_GUIDANCE_CACHE_TTL_MS
    );

    if (cached.found && cached.data) {
      setResources(cached.data.resources || []);
      setHasWarmCache(true);
      setIsCacheHydrated(true);
      return;
    }

    setHasWarmCache(false);
    setIsCacheHydrated(true);
  }, [cacheKey]);

  useEffect(() => {
    if (!cacheKey || !isCacheHydrated) return;
    if (!hasWarmCache && !hasLoadedFromServer) return;

    writeCachedData<StudentGuidanceCachePayload>(cacheKey, {
      resources,
    });
  }, [cacheKey, isCacheHydrated, hasLoadedFromServer, hasWarmCache, resources]);

  const loadResources = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    if (!user?.schoolId) {
      if (loadRequestIdRef.current === requestId) {
        setResources([]);
        setLoadResourcesError('');
      }
      return;
    }

    const fetchRows = async () =>
      supabase
        .from('resources')
        .select('id,title,description,category,type,content,created_at')
        .eq('school_id', user.schoolId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

    const { data, error } = await fetchRows();

    if (loadRequestIdRef.current !== requestId) return;

    if (error || !data) {
      setLoadResourcesError(error?.message || 'Unable to load guidance resources. Please retry.');
      return;
    }

    let mapped = data.map((row, index) => ({
      id: row.id,
      title: row.title,
      description: row.description,
      category: row.category,
      type: formatType(row.type),
      readTime: estimateReadTime(row.content || row.description),
      featured: index < 2,
    }));

    // Prevent transient empty flashes caused by session refresh race.
    if (mapped.length === 0 && resourcesRef.current.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 450));
      const { data: retryData, error: retryError } = await fetchRows();

      if (loadRequestIdRef.current !== requestId) return;

      if (!retryError && retryData) {
        mapped = retryData.map((row, index) => ({
          id: row.id,
          title: row.title,
          description: row.description,
          category: row.category,
          type: formatType(row.type),
          readTime: estimateReadTime(row.content || row.description),
          featured: index < 2,
        }));
      }
    }

    if (mapped.length === 0 && resourcesRef.current.length > 0) {
      emptyFetchStreakRef.current += 1;
      if (emptyFetchStreakRef.current < 2) {
        setLoadResourcesError('');
        setHasLoadedFromServer(true);
        return;
      }
    } else {
      emptyFetchStreakRef.current = 0;
    }

    setLoadResourcesError('');
    setHasLoadedFromServer(true);
    setResources(mapped);
  }, [user?.schoolId]);

  useEffect(() => {
    if (!user?.id) {
      setResources([]);
      setIsLoadingResources(false);
      return;
    }

    if (!isCacheHydrated) return;

    setIsLoadingResources(!hasWarmCacheRef.current);
    void loadResources().finally(() => setIsLoadingResources(false));
    return startVisibilityAwarePolling(() => loadResources(), 15000);
  }, [user?.id, loadResources, isCacheHydrated]);

  const filteredResources = resources.filter((resource) => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch =
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredResources = resources.filter((resource) => resource.featured);
  const isInitializing = !isCacheHydrated && !!user?.id;
  const showLoadingState = (isLoadingResources || isInitializing) && resources.length === 0;

  const getCategoryStyles = (category: string) => {
    return (
      categoryStyleMap[category] || {
        label: 'General',
        textColor: 'text-muted-foreground',
        accent: 'bg-muted',
        iconBox: 'bg-muted',
        iconColor: 'text-muted-foreground',
      }
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'college':
        return (
          <svg data-component-id="src/app/student/guidance/page.tsx:270:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="270" data-component-end-line="277" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path data-component-id="src/app/student/guidance/page.tsx:271:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="271" data-component-end-line="276" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 14l9-5-9-5-9 5 9 5zm0 0l7.5-4.167M12 14v7"
            />
          </svg>
        );
      case 'career':
        return (
          <svg data-component-id="src/app/student/guidance/page.tsx:281:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="281" data-component-end-line="288" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path data-component-id="src/app/student/guidance/page.tsx:282:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="282" data-component-end-line="287" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7h18M7 7V5a2 2 0 012-2h6a2 2 0 012 2v2m-2 0v10a2 2 0 01-2 2H9a2 2 0 01-2-2V7h10z"
            />
          </svg>
        );
      case 'academic':
        return (
          <svg data-component-id="src/app/student/guidance/page.tsx:292:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="292" data-component-end-line="299" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path data-component-id="src/app/student/guidance/page.tsx:293:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="293" data-component-end-line="298" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        );
      case 'wellness':
        return (
          <svg data-component-id="src/app/student/guidance/page.tsx:303:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="303" data-component-end-line="310" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path data-component-id="src/app/student/guidance/page.tsx:304:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="304" data-component-end-line="309" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 010 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        );
      default:
        return (
          <svg data-component-id="src/app/student/guidance/page.tsx:314:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="314" data-component-end-line="321" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path data-component-id="src/app/student/guidance/page.tsx:315:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="315" data-component-end-line="320" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        );
    }
  };

  return (
    <div data-component-id="src/app/student/guidance/page.tsx:327:4" data-component-path="src/app/student/guidance/page.tsx" data-component-line="327" data-component-end-line="569" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      {/* Header */}
      <div data-component-id="src/app/student/guidance/page.tsx:329:6" data-component-path="src/app/student/guidance/page.tsx" data-component-line="329" data-component-end-line="336" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <h1 data-component-id="src/app/student/guidance/page.tsx:330:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="330" data-component-end-line="332" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-2xl%20sm%3Atext-3xl%20font-bold%20text-foreground%20font-heading%22%2C%22textContent%22%3A%22Guidance%20Resources%22%7D" className="text-2xl sm:text-3xl font-bold text-foreground font-heading">
          Guidance Resources
        </h1>
        <p data-component-id="src/app/student/guidance/page.tsx:333:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="333" data-component-end-line="335" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Explore%20resources%20for%20college%20prep%2C%20career%20planning%2C%20and%20more%22%7D" className="text-muted-foreground mt-1">
          Explore resources for college prep, career planning, and more
        </p>
      </div>

      {/* Search */}
      <div data-component-id="src/app/student/guidance/page.tsx:339:6" data-component-path="src/app/student/guidance/page.tsx" data-component-line="339" data-component-end-line="360" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-xl%20border%20border-border%20bg-card%20p-1%22%7D" className="relative rounded-xl border border-border bg-card p-1">
        <svg data-component-id="src/app/student/guidance/page.tsx:340:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="340" data-component-end-line="352" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20w-5%20h-5%20text-muted-foreground%22%7D"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path data-component-id="src/app/student/guidance/page.tsx:346:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="346" data-component-end-line="351" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input data-component-id="src/app/student/guidance/page.tsx:353:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="353" data-component-end-line="359" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-11%20pr-4%20py-2.5%20rounded-lg%20border%20border-transparent%20bg-card%20text-foreground%20placeholder%3Atext-muted-foreground%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
          type="text"
          placeholder="Search resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-transparent bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {loadResourcesError && (
        <div data-component-id="src/app/student/guidance/page.tsx:363:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="363" data-component-end-line="381" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-4%20bg-destructive%2F10%20border%20border-destructive%2F20%20rounded-lg%22%7D" className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <svg data-component-id="src/app/student/guidance/page.tsx:364:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="364" data-component-end-line="376" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-destructive%20flex-shrink-0%22%7D"
            className="w-5 h-5 text-destructive flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path data-component-id="src/app/student/guidance/page.tsx:370:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="370" data-component-end-line="375" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-2.5L13.73 4.5c-.77-.83-2.69-.83-3.46 0L3.34 16.5c-.77.83.19 2.5 1.73 2.5z"
            />
          </svg>
          <p data-component-id="src/app/student/guidance/page.tsx:377:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="377" data-component-end-line="377" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-destructive%20font-medium%22%7D" className="text-sm text-destructive font-medium">{loadResourcesError}</p>
          <Button data-component-id="src/app/student/guidance/page.tsx:378:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="378" data-component-end-line="380" data-component-file="page.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22ml-auto%22%2C%22textContent%22%3A%22Retry%22%7D" size="sm" variant="outline" className="ml-auto" onClick={() => void loadResources()}>
            Retry
          </Button>
        </div>
      )}

      {showLoadingState && (
        <div data-component-id="src/app/student/guidance/page.tsx:385:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="385" data-component-end-line="387" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%20bg-card%20rounded-xl%20border%20border-border%22%7D" className="text-center py-12 bg-card rounded-xl border border-border">
          <p data-component-id="src/app/student/guidance/page.tsx:386:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="386" data-component-end-line="386" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20resources...%22%7D" className="text-muted-foreground">Loading resources...</p>
        </div>
      )}

      {/* Featured Resources */}
      {selectedCategory === 'all' &&
        !searchQuery &&
        featuredResources.length > 0 &&
        !showLoadingState && (
        <ContentCard data-component-id="src/app/student/guidance/page.tsx:395:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="395" data-component-end-line="445" data-component-file="page.tsx" data-component-name="ContentCard" data-component-content="%7B%22elementName%22%3A%22ContentCard%22%7D"
          title="Featured Resources"
          description="Recommended starting points picked for students."
        >
          <div data-component-id="src/app/student/guidance/page.tsx:399:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="399" data-component-end-line="444" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20sm%3Agrid-cols-2%20gap-4%22%7D" className="grid sm:grid-cols-2 gap-4">
            {featuredResources.map((resource) => {
              const styles = getCategoryStyles(resource.category);

              return (
                <Card data-component-id="src/app/student/guidance/page.tsx:404:16" data-component-path="src/app/student/guidance/page.tsx" data-component-line="404" data-component-end-line="441" data-component-file="page.tsx" data-component-name="Card" data-component-content="%7B%22elementName%22%3A%22Card%22%2C%22className%22%3A%22p-0%20overflow-hidden%22%7D" key={resource.id} className="p-0 overflow-hidden" hover>
                  <div data-component-id="src/app/student/guidance/page.tsx:405:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="405" data-component-end-line="405" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`h-1 ${styles.accent}`} />
                  <div data-component-id="src/app/student/guidance/page.tsx:406:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="406" data-component-end-line="440" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-5%20space-y-4%22%7D" className="p-5 space-y-4">
                    <div data-component-id="src/app/student/guidance/page.tsx:407:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="407" data-component-end-line="428" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-3%22%7D" className="flex items-start justify-between gap-3">
                      <div data-component-id="src/app/student/guidance/page.tsx:408:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="408" data-component-end-line="424" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%20min-w-0%22%7D" className="flex items-start gap-3 min-w-0">
                        <div data-component-id="src/app/student/guidance/page.tsx:409:24" data-component-path="src/app/student/guidance/page.tsx" data-component-line="409" data-component-end-line="413" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${styles.iconBox} ${styles.iconColor}`}
                        >
                          {getCategoryIcon(resource.category)}
                        </div>
                        <div data-component-id="src/app/student/guidance/page.tsx:414:24" data-component-path="src/app/student/guidance/page.tsx" data-component-line="414" data-component-end-line="423" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
                          <p data-component-id="src/app/student/guidance/page.tsx:415:26" data-component-path="src/app/student/guidance/page.tsx" data-component-line="415" data-component-end-line="419" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
                            className={`text-[11px] font-semibold uppercase tracking-wide ${styles.textColor}`}
                          >
                            {styles.label}
                          </p>
                          <h3 data-component-id="src/app/student/guidance/page.tsx:420:26" data-component-path="src/app/student/guidance/page.tsx" data-component-line="420" data-component-end-line="422" data-component-file="page.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-foreground%20mt-2%20leading-snug%22%7D" className="font-semibold text-foreground mt-2 leading-snug">
                            {resource.title}
                          </h3>
                        </div>
                      </div>
                      <span data-component-id="src/app/student/guidance/page.tsx:425:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="425" data-component-end-line="427" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20whitespace-nowrap%22%7D" className="text-xs text-muted-foreground whitespace-nowrap">
                        {resource.readTime}
                      </span>
                    </div>

                    <p data-component-id="src/app/student/guidance/page.tsx:430:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="430" data-component-end-line="432" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20leading-relaxed%22%7D" className="text-sm text-muted-foreground leading-relaxed">
                      {resource.description}
                    </p>

                    <div data-component-id="src/app/student/guidance/page.tsx:434:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="434" data-component-end-line="439" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20pt-3%20border-t%20border-border%2F70%22%7D" className="flex items-center justify-between pt-3 border-t border-border/70">
                      <span data-component-id="src/app/student/guidance/page.tsx:435:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="435" data-component-end-line="435" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%7D" className="text-xs text-muted-foreground">{resource.type}</span>
                      <Button data-component-id="src/app/student/guidance/page.tsx:436:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="436" data-component-end-line="438" data-component-file="page.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22textContent%22%3A%22Open%20Resource%22%7D" variant="outline" size="sm">
                        Open Resource
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </ContentCard>
      )}

      {/* Categories */}
      <div data-component-id="src/app/student/guidance/page.tsx:449:6" data-component-path="src/app/student/guidance/page.tsx" data-component-line="449" data-component-end-line="465" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-b%20border-border%22%7D" className="border-b border-border">
        <div data-component-id="src/app/student/guidance/page.tsx:450:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="450" data-component-end-line="464" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-5%20px-1%20pb-2%22%7D" className="flex flex-wrap gap-5 px-1 pb-2">
          {categories.map((category) => (
            <button data-component-id="src/app/student/guidance/page.tsx:452:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="452" data-component-end-line="462" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`pb-1 border-b-2 text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resources Grid */}
      {!showLoadingState && <div data-component-id="src/app/student/guidance/page.tsx:468:28" data-component-path="src/app/student/guidance/page.tsx" data-component-line="468" data-component-end-line="540" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-4%22%7D" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResources.map((resource) => {
          const styles = getCategoryStyles(resource.category);

          return (
            <Card data-component-id="src/app/student/guidance/page.tsx:473:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="473" data-component-end-line="537" data-component-file="page.tsx" data-component-name="Card" data-component-content="%7B%22elementName%22%3A%22Card%22%2C%22className%22%3A%22p-0%20overflow-hidden%20h-full%22%7D" key={resource.id} className="p-0 overflow-hidden h-full" hover>
              <div data-component-id="src/app/student/guidance/page.tsx:474:14" data-component-path="src/app/student/guidance/page.tsx" data-component-line="474" data-component-end-line="474" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`h-1 ${styles.accent}`} />
              <div data-component-id="src/app/student/guidance/page.tsx:475:14" data-component-path="src/app/student/guidance/page.tsx" data-component-line="475" data-component-end-line="536" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-5%20flex%20flex-col%20h-full%22%7D" className="p-5 flex flex-col h-full">
                <div data-component-id="src/app/student/guidance/page.tsx:476:16" data-component-path="src/app/student/guidance/page.tsx" data-component-line="476" data-component-end-line="497" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-2%22%7D" className="flex items-start justify-between gap-2">
                  <div data-component-id="src/app/student/guidance/page.tsx:477:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="477" data-component-end-line="493" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%20min-w-0%22%7D" className="flex items-start gap-3 min-w-0">
                    <div data-component-id="src/app/student/guidance/page.tsx:478:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="478" data-component-end-line="482" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${styles.iconBox} ${styles.iconColor}`}
                    >
                      {getCategoryIcon(resource.category)}
                    </div>
                    <div data-component-id="src/app/student/guidance/page.tsx:483:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="483" data-component-end-line="492" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-w-0%22%7D" className="min-w-0">
                      <h3 data-component-id="src/app/student/guidance/page.tsx:484:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="484" data-component-end-line="486" data-component-file="page.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-foreground%20leading-snug%20line-clamp-2%22%7D" className="font-semibold text-foreground leading-snug line-clamp-2">
                        {resource.title}
                      </h3>
                      <p data-component-id="src/app/student/guidance/page.tsx:487:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="487" data-component-end-line="491" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
                        className={`mt-1 text-[11px] font-semibold uppercase tracking-wide ${styles.textColor}`}
                      >
                        {styles.label}
                      </p>
                    </div>
                  </div>
                  <span data-component-id="src/app/student/guidance/page.tsx:494:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="494" data-component-end-line="496" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20whitespace-nowrap%22%7D" className="text-xs text-muted-foreground whitespace-nowrap">
                    {resource.type}
                  </span>
                </div>

                <p data-component-id="src/app/student/guidance/page.tsx:499:16" data-component-path="src/app/student/guidance/page.tsx" data-component-line="499" data-component-end-line="501" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-4%20mb-4%20line-clamp-3%20leading-relaxed%20flex-1%22%7D" className="text-sm text-muted-foreground mt-4 mb-4 line-clamp-3 leading-relaxed flex-1">
                  {resource.description}
                </p>

                <div data-component-id="src/app/student/guidance/page.tsx:503:16" data-component-path="src/app/student/guidance/page.tsx" data-component-line="503" data-component-end-line="535" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20pt-3%20border-t%20border-border%2F70%22%7D" className="flex items-center justify-between pt-3 border-t border-border/70">
                  <span data-component-id="src/app/student/guidance/page.tsx:504:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="504" data-component-end-line="514" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20flex%20items-center%20gap-1%22%7D" className="text-xs text-muted-foreground flex items-center gap-1">
                    <svg data-component-id="src/app/student/guidance/page.tsx:505:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="505" data-component-end-line="512" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path data-component-id="src/app/student/guidance/page.tsx:506:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="506" data-component-end-line="511" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {resource.readTime}
                  </span>
                  <Button data-component-id="src/app/student/guidance/page.tsx:515:18" data-component-path="src/app/student/guidance/page.tsx" data-component-line="515" data-component-end-line="534" data-component-file="page.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22text-primary%20hover%3Atext-primary%20hover%3Abg-primary%2F10%22%2C%22textContent%22%3A%22View%22%7D"
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary hover:bg-primary/10"
                  >
                    View
                    <svg data-component-id="src/app/student/guidance/page.tsx:521:20" data-component-path="src/app/student/guidance/page.tsx" data-component-line="521" data-component-end-line="533" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%20ml-1%22%7D"
                      className="w-4 h-4 ml-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path data-component-id="src/app/student/guidance/page.tsx:527:22" data-component-path="src/app/student/guidance/page.tsx" data-component-line="527" data-component-end-line="532" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>}

      {filteredResources.length === 0 && !showLoadingState && (
        <div data-component-id="src/app/student/guidance/page.tsx:543:8" data-component-path="src/app/student/guidance/page.tsx" data-component-line="543" data-component-end-line="567" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
          <svg data-component-id="src/app/student/guidance/page.tsx:544:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="544" data-component-end-line="556" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-12%20h-12%20mx-auto%20text-muted-foreground%20mb-4%22%7D"
            className="w-12 h-12 mx-auto text-muted-foreground mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path data-component-id="src/app/student/guidance/page.tsx:550:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="550" data-component-end-line="555" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p data-component-id="src/app/student/guidance/page.tsx:557:10" data-component-path="src/app/student/guidance/page.tsx" data-component-line="557" data-component-end-line="561" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%7D" className="text-muted-foreground">
            {searchQuery || selectedCategory !== 'all'
              ? 'No resources found matching your criteria'
              : 'No published resources yet'}
          </p>
          {!searchQuery && selectedCategory === 'all' && (
            <p data-component-id="src/app/student/guidance/page.tsx:563:12" data-component-path="src/app/student/guidance/page.tsx" data-component-line="563" data-component-end-line="565" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Your%20counselor%20can%20publish%20resources%20and%20they%20will%20appear%20here.%22%7D" className="text-sm text-muted-foreground mt-1">
              Your counselor can publish resources and they will appear here.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
